import React, { useState, useEffect } from 'react';
import IndustryManagerModal from './IndustryManagerModal';
import { colors } from '../theme/tokens';

const STATUS_COLORS = {
  stable: '#2fb65c',
  unstable: '#f4d03f',
  competitor: '#e74c3c',
  lead: '#ff69b4',
  technical: '#9b59b6',
  inactive: '#888888'
};

const STATUS_LABELS = {
  stable: 'Stable Client',
  unstable: 'Unstable Client',
  competitor: 'Buying from Competitor',
  lead: 'New Lead',
  technical: 'Technical Difficulty',
  inactive: 'Inactive'
};

const getTimeAgo = (date) => {
  if (!date) return '—';
  const now = new Date();
  const past = new Date(date);
  const diffMs = now - past;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks < 4) return `${diffWeeks}w ago`;
  return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
};

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
};

const MessageBoard = ({
  client,
  onClose,
  onStatusChange,
  onAddMessage,
  onQuickAction,
  onArchive,
  onMarkRead,
  onClaim,
  onReassign,
  onRename,
  onUpdateErpCode,
  onSetIndustry,
  onSaveIndustry,
  onDeleteIndustry,
  onStartRelocate,
  onJumpToMarker,
  onSetRegion,
  currentUser,
  salesTeam = [],
  industries = [],
  regions = [],
  leadActions = []
}) => {
  const [messageText, setMessageText] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Collapsible "more details" section
  const [showMore, setShowMore] = useState(false);

  // Reassign
  const [reassignUserId, setReassignUserId] = useState('');
  const [reassigning, setReassigning] = useState(false);

  // Claim
  const [claiming, setClaiming] = useState(false);

  // Edit name
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(client.clientName);

  // Edit ERP code
  const [editingErpCode, setEditingErpCode] = useState(false);
  const [erpCodeInput, setErpCodeInput] = useState(client.erpCode || '');

  // Industry
  const [editingIndustry, setEditingIndustry] = useState(false);
  const [showIndustryProducts, setShowIndustryProducts] = useState(false);
  const [showIndustryManager, setShowIndustryManager] = useState(false);

  // Region
  const [editingRegion, setEditingRegion] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (client && client._id) onMarkRead(client._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client._id]);

  useEffect(() => {
    setNameInput(client.clientName);
    setEditingName(false);
  }, [client.clientName]);

  useEffect(() => {
    setErpCodeInput(client.erpCode || '');
    setEditingErpCode(false);
  }, [client.erpCode]);

  useEffect(() => {
    setEditingIndustry(false);
    setShowIndustryProducts(false);
  }, [client._id]);

  const handleSubmitMessage = (e) => {
    e.preventDefault();
    if (messageText.trim()) {
      onAddMessage(client._id, messageText);
      setMessageText('');
    }
  };

  const handleStatusChange = (e) => onStatusChange(client._id, e.target.value);
  const handleArchive = () => onArchive(client._id);

  const [quickBusy, setQuickBusy] = useState(null);
  const [quickSelected, setQuickSelected] = useState('');
  const handleQuickAction = async (action) => {
    if (!onQuickAction || quickBusy) return;
    setQuickBusy(action);
    try {
      await onQuickAction(client._id, action);
      setQuickSelected('');
    } finally {
      setQuickBusy(null);
    }
  };

  const handleClaim = async () => {
    setClaiming(true);
    await onClaim(client._id);
    setClaiming(false);
  };

  const handleReassign = async () => {
    if (!reassignUserId) return;
    setReassigning(true);
    await onReassign(client._id, reassignUserId);
    setReassignUserId('');
    setReassigning(false);
  };

  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === client.clientName) { setEditingName(false); setNameInput(client.clientName); return; }
    await onRename(client._id, trimmed);
    setEditingName(false);
  };

  const handleCancelEditName = () => { setEditingName(false); setNameInput(client.clientName); };

  const handleSaveErpCode = async () => {
    const trimmed = erpCodeInput.trim().toUpperCase();
    if (trimmed === (client.erpCode || '').toUpperCase()) { setEditingErpCode(false); return; }
    await onUpdateErpCode(client._id, trimmed || null);
    setEditingErpCode(false);
  };

  const handleCancelErpCode = () => { setEditingErpCode(false); setErpCodeInput(client.erpCode || ''); };

  const buildErpUrl = (code) => {
    const padded = code.padEnd(10, ' ');
    return `https://par.brilliantcloud.online/PAR/Main?CompanyID=PAR+Group&ScreenId=AR303000&AcctCD=${padded.replace(/ /g, '+')}`;
  };

  const handleIndustrySelect = async (e) => {
    const value = e.target.value;
    await onSetIndustry(client._id, value || null);
    setEditingIndustry(false);
    setShowIndustryProducts(false);
  };

  const handleRegionSelect = async (e) => {
    const value = e.target.value;
    if (onSetRegion) await onSetRegion(client._id, value || null);
    setEditingRegion(false);
  };

  const currentIndustry = industries.find(i => i.name === client.industry);

  const isSalesManager = currentUser?.salesRole === 'manager';
  const isClaimed = !!client.claimedBy;
  const timeAgo = getTimeAgo(client.lastActionDate);
  const isOld = new Date() - new Date(client.lastActionDate) > 30 * 24 * 60 * 60 * 1000;
  const containerStyle = isMobile ? styles.mobileContainer : styles.sidebarContainer;

  return (
    <>
      {isMobile && <div style={styles.overlay} onClick={onClose} />}
      <div style={containerStyle}>

        {/* ── Header ── */}
        <div style={styles.header}>
          <div style={{ flex: 1, minWidth: 0 }}>

            {/* Client name */}
            {editingName ? (
              <div style={styles.nameEditRow}>
                <input
                  autoFocus value={nameInput}
                  onChange={e => setNameInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') handleCancelEditName(); }}
                  style={styles.nameInput}
                />
                <button onClick={handleSaveName} style={styles.nameSaveBtn}>Save</button>
                <button onClick={handleCancelEditName} style={styles.nameCancelBtn}>✕</button>
              </div>
            ) : (
              <div style={styles.nameLine}>
                <h2 style={styles.clientName}>{client.clientName}</h2>
                {isSalesManager && (
                  <button onClick={() => setEditingName(true)} style={styles.editNameBtn} title="Edit client name">✏️</button>
                )}
              </div>
            )}

            {/* ERP customer code */}
            {client.erpCode && !editingErpCode ? (
              <div style={styles.erpRow}>
                <a href={buildErpUrl(client.erpCode)} target="_blank" rel="noopener noreferrer" style={styles.erpLink}>
                  🔗 See in System
                  <span style={styles.erpCode}>{client.erpCode}</span>
                </a>
                {isSalesManager && (
                  <button onClick={() => setEditingErpCode(true)} style={styles.erpEditBtn} title="Edit customer code">✏️</button>
                )}
              </div>
            ) : editingErpCode ? (
              <div style={styles.erpEditRow}>
                <input
                  autoFocus value={erpCodeInput}
                  onChange={e => setErpCodeInput(e.target.value.toUpperCase())}
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveErpCode(); if (e.key === 'Escape') handleCancelErpCode(); }}
                  placeholder="e.g. C03036"
                  style={styles.erpInput}
                  maxLength={10}
                />
                <button onClick={handleSaveErpCode} style={styles.nameSaveBtn}>Save</button>
                <button onClick={handleCancelErpCode} style={styles.nameCancelBtn}>✕</button>
              </div>
            ) : isSalesManager ? (
              <button onClick={() => setEditingErpCode(true)} style={styles.erpAddBtn}>+ Add Customer Code</button>
            ) : null}

            {/* ── Assigned to (always visible) ── */}
            <div style={styles.assignedRow}>
              <span style={styles.metaLabel}>Assigned to</span>
              {isClaimed ? (
                <span style={styles.metaValue}>
                  <span style={styles.claimedBadge}>✔</span>
                  {client.claimedByName} · {formatDate(client.claimedAt)}
                </span>
              ) : (
                <span style={{ ...styles.metaValue, color: colors.textMuted, fontStyle: 'italic' }}>Unclaimed</span>
              )}
            </div>

            {/* ── Industry field ── */}
            <div style={styles.industryRow}>
              <span style={styles.metaLabel}>Industry</span>
              <div style={styles.industryRight}>
                {editingIndustry ? (
                  <select
                    autoFocus
                    defaultValue={client.industry || ''}
                    onChange={handleIndustrySelect}
                    onBlur={() => setEditingIndustry(false)}
                    style={styles.industrySelect}
                  >
                    <option value="">— None —</option>
                    {industries.map(ind => (
                      <option key={ind._id} value={ind.name}>{ind.name}</option>
                    ))}
                  </select>
                ) : client.industry ? (
                  <button
                    onClick={() => setShowIndustryProducts(p => !p)}
                    style={styles.industryChip}
                  >
                    {client.industry}
                    <span style={styles.industryChevron}>{showIndustryProducts ? '▲' : '▼'}</span>
                  </button>
                ) : (
                  <span style={{ color: colors.textFaint, fontSize: '12px', fontStyle: 'italic' }}>—</span>
                )}

                {/* Change / set button — all sales team members */}
                {!editingIndustry && (
                  <button onClick={() => setEditingIndustry(true)} style={styles.industryChangeBtn} title="Change industry">
                    {client.industry ? '✏️' : '+ Set'}
                  </button>
                )}

                {/* Manage industries gear — managers only */}
                {isSalesManager && (
                  <button onClick={() => setShowIndustryManager(true)} style={styles.industryGearBtn} title="Manage industries">⚙️</button>
                )}
              </div>
            </div>

            {/* Products panel — shown when industry chip is clicked */}
            {showIndustryProducts && client.industry && (
              <div style={styles.productsPanel}>
                <div style={styles.productsPanelTitle}>
                  What we supply to <strong>{client.industry}</strong>:
                </div>
                {currentIndustry && currentIndustry.products.length > 0 ? (
                  <ul style={styles.productsList}>
                    {currentIndustry.products.map((p, i) => (
                      <li key={i} style={styles.productsListItem}>{p}</li>
                    ))}
                  </ul>
                ) : (
                  <div style={styles.noProducts}>No products listed for this industry yet.</div>
                )}
              </div>
            )}

            {/* ── Region field ── */}
            <div style={styles.industryRow}>
              <span style={styles.metaLabel}>Region</span>
              <div style={styles.industryRight}>
                {editingRegion ? (
                  <select
                    autoFocus
                    defaultValue={client.region?.toString() || client.region || ''}
                    onChange={handleRegionSelect}
                    onBlur={() => setEditingRegion(false)}
                    style={styles.industrySelect}
                  >
                    <option value="">— None —</option>
                    {regions.map(r => (
                      <option key={r._id} value={r._id}>{r.name}</option>
                    ))}
                  </select>
                ) : client.regionName ? (
                  <span style={styles.industryChip}>{client.regionName}</span>
                ) : (
                  <span style={{ color: colors.textFaint, fontSize: '12px', fontStyle: 'italic' }}>—</span>
                )}
                {/* Change button — managers and rubber only */}
                {isSalesManager && !editingRegion && regions.length > 0 && (
                  <button onClick={() => setEditingRegion(true)} style={styles.industryChangeBtn} title="Change region">
                    {client.regionName ? '✏️' : '+ Set'}
                  </button>
                )}
              </div>
            </div>

            {/* ── Last action ── */}
            <div style={styles.timeAgo}>
              Last action:{' '}
              <span style={{ color: isOld ? '#e74c3c' : colors.textMuted, fontWeight: isOld ? '700' : '500' }}>
                {timeAgo}
              </span>
            </div>

            {/* ── More Details toggle ── */}
            <button onClick={() => setShowMore(p => !p)} style={styles.moreToggle}>
              {showMore ? '▲ Less Details' : '▼ More Details'}
            </button>

            {/* ── Collapsible section ── */}
            {showMore && (
              <div style={styles.moreSection}>
                {/* Created by */}
                <div style={styles.metaRow}>
                  <span style={styles.metaLabel}>Created by</span>
                  <span style={styles.metaValue}>
                    {client.createdByName || '—'} · {formatDate(client.createdAt)}
                  </span>
                </div>

                {/* Move Location */}
                <button onClick={() => onStartRelocate(client)} style={styles.relocateBtn}>
                  📍 Move Location
                </button>

                {/* Jump to marker on map */}
                {onJumpToMarker && (
                  <button onClick={() => onJumpToMarker(client)} style={styles.jumpBtn}>
                    🗺️ Jump to Marker
                  </button>
                )}

                {/* Claim button — visible for any unclaimed client regardless of status */}
                {!isClaimed && (
                  <button onClick={handleClaim} disabled={claiming} style={styles.claimBtn}>
                    {claiming ? 'Claiming...' : '🙋 Claim This Lead'}
                  </button>
                )}

                {/* Reassign — managers only */}
                {isSalesManager && (
                  <div style={styles.reassignRow}>
                    <select
                      value={reassignUserId}
                      onChange={e => setReassignUserId(e.target.value)}
                      style={styles.reassignSelect}
                    >
                      <option value="">{isClaimed ? 'Reassign to...' : 'Assign to...'}</option>
                      {salesTeam.map(member => (
                        <option key={member._id} value={member._id}>
                          {member.name} ({member.salesRole === 'manager' ? 'Manager' : 'Rep'})
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleReassign}
                      disabled={!reassignUserId || reassigning}
                      style={styles.reassignBtn}
                    >
                      {reassigning ? '...' : isClaimed ? 'Reassign' : 'Assign'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        {/* ── Status ── */}
        <div style={styles.statusSection}>
          <label style={styles.label}>Client Status:</label>
          <select
            value={client.status}
            onChange={handleStatusChange}
            style={{ ...styles.statusSelect, borderColor: STATUS_COLORS[client.status] }}
          >
            {Object.entries(STATUS_LABELS).map(([status, label]) => (
              <option key={status} value={status}>{label}</option>
            ))}
          </select>
          <div style={{ ...styles.statusIndicator, background: STATUS_COLORS[client.status] }} />
        </div>

        {/* ── Messages ── */}
        <div style={styles.messagesSection}>
          <h3 style={styles.sectionTitle}>Messages</h3>
          <div style={styles.messagesContainer}>
            {client.messages && client.messages.length > 0 ? (
              [...client.messages]
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map((message, index) => (
                  message.isSystem ? (
                    <div key={index} style={styles.systemMessage}>
                      <span style={styles.systemIcon}>🔔</span>
                      <span style={styles.systemText}>{message.text}</span>
                      <span style={styles.systemTime}>{getTimeAgo(message.createdAt)}</span>
                    </div>
                  ) : (
                    <div key={index} style={styles.message}>
                      <div style={styles.messageHeader}>
                        {message.userPicture && (
                          <img src={message.userPicture} alt={message.userName} style={styles.avatar} />
                        )}
                        <div>
                          <div style={styles.userName}>{message.userName}</div>
                          <div style={styles.messageTime}>{getTimeAgo(message.createdAt)}</div>
                        </div>
                      </div>
                      <div style={styles.messageText}>{message.text}</div>
                    </div>
                  )
                ))
            ) : (
              <div style={styles.noMessages}>No messages yet</div>
            )}
          </div>
        </div>

        {/* ── Quick actions (logged & counted toward analytics points) ── */}
        {onQuickAction && leadActions.length > 0 && (
          <div style={styles.quickBar}>
            <span style={styles.quickLabel}>Quick log</span>
            <div style={styles.quickRow}>
              <select
                value={quickSelected}
                onChange={(e) => setQuickSelected(e.target.value)}
                disabled={!!quickBusy}
                style={styles.quickSelect}
                aria-label="Choose an action to log"
              >
                <option value="">Select an action…</option>
                {leadActions.map((qa) => (
                  <option key={qa.key} value={qa.key}>
                    {qa.icon ? `${qa.icon} ` : ''}{qa.label}{qa.points ? ` (+${qa.points})` : ''}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => quickSelected && handleQuickAction(quickSelected)}
                disabled={!quickSelected || !!quickBusy}
                style={{ ...styles.quickBtn, opacity: (!quickSelected || quickBusy) ? 0.5 : 1 }}
                title="Log the selected action"
              >
                {quickBusy ? 'Logging…' : 'Log'}
              </button>
            </div>
          </div>
        )}

        {/* ── Compose ── */}
        <form onSubmit={handleSubmitMessage} style={styles.messageForm}>
          <textarea
            value={messageText}
            onChange={e => setMessageText(e.target.value)}
            placeholder="Leave a message..."
            style={styles.textarea}
            rows={3}
          />
          <button type="submit" style={styles.sendBtn}>Send Message</button>
        </form>

        {/* ── Archive (managers only) ── */}
        {isSalesManager && (
          <div style={styles.archiveSection}>
            <button onClick={handleArchive} style={styles.archiveBtn}>🗄️ Archive Client</button>
          </div>
        )}
      </div>

      {/* Industry Manager Modal */}
      {showIndustryManager && (
        <IndustryManagerModal
          industries={industries}
          onSave={onSaveIndustry}
          onDelete={onDeleteIndustry}
          onClose={() => setShowIndustryManager(false)}
        />
      )}
    </>
  );
};

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: colors.overlay, zIndex: 9998
  },
  sidebarContainer: {
    position: 'fixed', top: 0, right: 0,
    width: '420px', height: '100vh',
    background: colors.surfaceSolid, boxShadow: '-2px 0 10px rgba(0,0,0,0.2)',
    zIndex: 9999, display: 'flex', flexDirection: 'column', overflowY: 'auto'
  },
  mobileContainer: {
    position: 'fixed', top: '50%', left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '92%', maxWidth: '500px', maxHeight: '88vh',
    background: colors.surfaceSolid, borderRadius: '15px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
    zIndex: 9999, display: 'flex', flexDirection: 'column', overflowY: 'auto'
  },
  header: {
    padding: '16px 18px',
    borderBottom: `2px solid ${colors.border}`,
    display: 'flex', gap: '10px',
    background: colors.surfaceSoft
  },
  nameLine: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' },
  clientName: { margin: 0, fontSize: '20px', color: colors.text, fontWeight: '700' },
  editNameBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '13px', padding: '2px 4px', lineHeight: 1, color: colors.textFaint, flexShrink: 0
  },
  nameEditRow: { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' },
  nameInput: {
    flex: 1, fontSize: '16px', fontWeight: '700', color: colors.text,
    border: '2px solid #2fb65c', borderRadius: '6px',
    padding: '4px 8px', outline: 'none', minWidth: 0,
    background: colors.inputBg
  },
  nameSaveBtn: {
    padding: '4px 12px', background: '#2fb65c', color: 'white',
    border: 'none', borderRadius: '6px', fontSize: '12px',
    fontWeight: '600', cursor: 'pointer', flexShrink: 0
  },
  nameCancelBtn: {
    padding: '4px 8px', background: 'none', color: colors.textMuted,
    border: `1px solid ${colors.border}`, borderRadius: '6px', fontSize: '12px',
    cursor: 'pointer', flexShrink: 0
  },
  erpRow: { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' },
  erpLink: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '4px 10px', background: '#1E1E1E', color: 'white',
    borderRadius: '6px', fontSize: '11px', fontWeight: '600',
    textDecoration: 'none'
  },
  erpCode: {
    background: 'rgba(255,255,255,0.15)', borderRadius: '4px',
    padding: '1px 5px', fontSize: '11px', fontFamily: 'monospace'
  },
  erpEditBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '12px', padding: '2px', color: colors.textFaint, lineHeight: 1
  },
  erpAddBtn: {
    marginBottom: '8px', padding: '3px 8px',
    background: 'none', color: colors.textFaint,
    border: `1px dashed ${colors.border}`, borderRadius: '5px',
    fontSize: '11px', fontWeight: '600', cursor: 'pointer'
  },
  erpEditRow: { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' },
  erpInput: {
    flex: 1, fontSize: '13px', fontWeight: '700', fontFamily: 'monospace',
    color: colors.text, border: `2px solid ${colors.border}`, borderRadius: '6px',
    padding: '4px 8px', outline: 'none', minWidth: 0,
    background: colors.inputBg
  },
  assignedRow: {
    display: 'flex', gap: '8px', alignItems: 'baseline',
    fontSize: '12px', marginBottom: '6px'
  },
  industryRow: {
    display: 'flex', gap: '8px', alignItems: 'center',
    fontSize: '12px', marginBottom: '4px', flexWrap: 'wrap'
  },
  industryRight: {
    display: 'flex', alignItems: 'center', gap: '4px', flex: 1, flexWrap: 'wrap'
  },
  industrySelect: {
    padding: '4px 8px', fontSize: '12px', borderRadius: '6px',
    border: '2px solid #2fb65c', outline: 'none', cursor: 'pointer', background: colors.inputBg, color: colors.text
  },
  industryChip: {
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    padding: '3px 10px',
    background: '#eef7ff', color: '#1a6bb5',
    border: '1px solid #c5dff8', borderRadius: '20px',
    fontSize: '12px', fontWeight: '600', cursor: 'pointer'
  },
  industryChevron: { fontSize: '10px', opacity: 0.7 },
  industryChangeBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '12px', padding: '2px 4px', color: colors.textFaint, lineHeight: 1
  },
  industryGearBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '13px', padding: '2px 4px', color: colors.textFaint, lineHeight: 1, marginLeft: '2px'
  },
  productsPanel: {
    background: colors.surfaceSoft, border: `1px solid ${colors.borderSoft}`,
    borderRadius: '8px', padding: '10px 12px', marginBottom: '6px', marginTop: '2px'
  },
  productsPanelTitle: { fontSize: '12px', color: colors.textMuted, marginBottom: '6px' },
  productsList: { margin: 0, paddingLeft: '16px' },
  productsListItem: { fontSize: '12px', color: colors.text, marginBottom: '3px' },
  noProducts: { fontSize: '12px', color: colors.textFaint, fontStyle: 'italic' },
  timeAgo: { fontSize: '12px', color: colors.textMuted, marginTop: '6px', marginBottom: '4px' },
  moreToggle: {
    width: '100%', padding: '10px',
    background: '#eaf7ef', color: '#2a9e52',
    border: '1px solid #c3e8d0', borderRadius: '8px',
    cursor: 'pointer', fontSize: '13px', fontWeight: '700',
    marginTop: '8px', textAlign: 'center', letterSpacing: '0.2px'
  },
  moreSection: {
    marginTop: '8px', paddingTop: '8px',
    borderTop: '1px solid #ebebeb',
    display: 'flex', flexDirection: 'column', gap: '8px'
  },
  metaRow: { display: 'flex', gap: '8px', alignItems: 'baseline', fontSize: '12px' },
  metaLabel: { color: colors.textMuted, fontWeight: '600', minWidth: '72px', flexShrink: 0, fontSize: '12px' },
  metaValue: { color: colors.text, fontWeight: '500', fontSize: '12px' },
  claimedBadge: { color: '#2fb65c', fontWeight: '700', marginRight: '4px' },
  relocateBtn: {
    padding: '6px 12px', background: colors.surface, color: colors.textMuted,
    border: `1px solid ${colors.border}`, borderRadius: '6px',
    fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'inline-block'
  },
  jumpBtn: {
    padding: '6px 12px', background: '#eaf7ef', color: '#2a9e52',
    border: '1px solid #c3e8d0', borderRadius: '6px',
    fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'inline-block'
  },
  claimBtn: {
    padding: '7px 14px',
    background: 'linear-gradient(135deg, #ff69b4, #e91e8c)',
    color: 'white', border: 'none', borderRadius: '6px',
    fontSize: '12px', fontWeight: '700', cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(233,30,140,0.3)'
  },
  reassignRow: { display: 'flex', gap: '8px', alignItems: 'center' },
  reassignSelect: {
    flex: 1, padding: '6px 8px', borderRadius: '6px',
    border: `1px solid ${colors.border}`, fontSize: '12px', background: colors.inputBg, color: colors.text, cursor: 'pointer'
  },
  reassignBtn: {
    padding: '6px 12px', background: '#1E1E1E', color: 'white',
    border: 'none', borderRadius: '6px', fontSize: '12px',
    fontWeight: '600', cursor: 'pointer', flexShrink: 0
  },
  closeBtn: {
    background: 'none', border: 'none', fontSize: '20px',
    cursor: 'pointer', color: colors.textMuted, padding: '2px', lineHeight: '1', flexShrink: 0
  },
  statusSection: {
    padding: '14px 18px', borderBottom: `1px solid ${colors.border}`, position: 'relative'
  },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: colors.text, marginBottom: '6px' },
  statusSelect: {
    width: '100%', padding: '9px 35px 9px 12px', fontSize: '14px',
    borderRadius: '8px', border: '2px solid', background: colors.inputBg, color: colors.text,
    cursor: 'pointer', appearance: 'none', fontWeight: '500'
  },
  statusIndicator: {
    position: 'absolute', right: '26px', top: '46px',
    width: '18px', height: '18px', borderRadius: '50%',
    border: `2px solid ${colors.surfaceSolid}`, boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
  },
  messagesSection: { flex: 1, padding: '14px 18px', overflowY: 'auto', minHeight: '160px' },
  sectionTitle: { margin: '0 0 12px 0', fontSize: '15px', fontWeight: '600', color: colors.text },
  messagesContainer: { display: 'flex', flexDirection: 'column', gap: '10px' },
  message: { background: colors.surfaceSoft, padding: '10px', borderRadius: '8px', border: `1px solid ${colors.borderSoft}` },
  systemMessage: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '6px 10px', borderRadius: '6px',
    background: colors.surfaceSoft, border: `1px solid ${colors.borderSoft}`, fontSize: '12px'
  },
  systemIcon: { fontSize: '12px', flexShrink: 0 },
  systemText: { flex: 1, color: colors.textMuted, fontStyle: 'italic' },
  systemTime: { color: colors.textFaint, fontSize: '11px', flexShrink: 0 },
  messageHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' },
  avatar: { width: '28px', height: '28px', borderRadius: '50%', border: `2px solid ${colors.surfaceSolid}` },
  userName: { fontWeight: '600', fontSize: '12px', color: colors.text },
  messageTime: { fontSize: '11px', color: colors.textMuted },
  messageText: { fontSize: '13px', color: colors.text, lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  noMessages: { textAlign: 'center', color: colors.textMuted, fontSize: '13px', padding: '25px', fontStyle: 'italic' },
  quickBar: {
    padding: '10px 18px', borderTop: `2px solid ${colors.border}`, background: colors.surfaceSoft
  },
  quickLabel: {
    display: 'block', fontSize: '11px', fontWeight: '700', color: colors.textMuted,
    textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '6px'
  },
  quickBtns: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  quickRow: { display: 'flex', gap: '8px', alignItems: 'center' },
  quickSelect: {
    flex: 1, minWidth: 0, padding: '8px 12px', borderRadius: '20px',
    background: colors.surface, color: colors.text,
    border: `1px solid ${colors.border}`, fontSize: '12px', fontWeight: '600',
    cursor: 'pointer'
  },
  quickBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '8px 16px', borderRadius: '20px',
    background: colors.parGreen, color: '#fff',
    border: `1px solid ${colors.parGreen}`, fontSize: '12px', fontWeight: '700',
    cursor: 'pointer', flexShrink: 0
  },
  quickIcon: { fontSize: '13px' },
  messageForm: { padding: '14px 18px', borderTop: `2px solid ${colors.border}`, background: colors.surfaceSoft },
  textarea: {
    width: '100%', padding: '10px', fontSize: '13px', borderRadius: '8px',
    border: `1px solid ${colors.border}`, resize: 'vertical', fontFamily: 'inherit', marginBottom: '8px',
    boxSizing: 'border-box', background: colors.inputBg, color: colors.text
  },
  sendBtn: {
    width: '100%', padding: '10px', background: '#2fb65c', color: 'white',
    border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600',
    cursor: 'pointer'
  },
  archiveSection: { padding: '10px 18px', borderTop: `2px solid ${colors.border}`, background: colors.surfaceSoft },
  archiveBtn: {
    width: '100%', padding: '10px', background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
  }
};

export default MessageBoard;
